import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/useAuthStore';
import { logger } from '../utils/logger';

export interface ModuleDef {
  id: string;
  label: string;
  icon: string;
  defaultVisible: boolean;
  group?: 'core' | 'ventas' | 'operaciones' | 'analytics' | 'config';
}

/**
 * Módulos retirados del sidebar / «Editar módulos» (sigue existiendo fila en `modulos` / permisos).
 * `facturas`: la factura (PDF/recibo) se ve desde Pedidos, Ventas y Clientes — no como módulo aparte.
 */
export const RETIRED_MODULE_IDS: readonly string[] = ['pipeline', 'facturas'];

/** Module IDs must match Sidebar nav (and modulos.id where applicable). */
export const ALL_MODULES: ModuleDef[] = [
  { id: 'dashboard', label: 'Resumen Ejecutivo', icon: 'LayoutDashboard', defaultVisible: true, group: 'core' },
  { id: 'alertas', label: 'Alertas', icon: 'Bell', defaultVisible: true, group: 'core' },
  { id: 'calendario', label: 'Calendario', icon: 'Calendar', defaultVisible: true, group: 'operaciones' },
  /** Retirado del menú; permisos/RBAC pueden seguir usando el id. */
  { id: 'facturas', label: 'Facturas', icon: 'FileText', defaultVisible: false, group: 'ventas' },
  /** Misma fila en `modulos` / `rol_permisos` (ver migración `modulo_cobros_rbac`); inyección desde facturas si faltase la fila. */
  { id: 'cobros', label: 'Cobros y Créditos', icon: 'CreditCard', defaultVisible: true, group: 'ventas' },
  { id: 'gastos', label: 'Gastos', icon: 'Banknotes', defaultVisible: true, group: 'operaciones' },
  { id: 'cierre_caja', label: 'Cierre de caja', icon: 'FileText', defaultVisible: true, group: 'operaciones' },
  { id: 'productos', label: 'Productos & Servicios', icon: 'Package', defaultVisible: true, group: 'operaciones' },
  { id: 'centro_diseno', label: 'Centro de Diseño', icon: 'PaintBrush', defaultVisible: true, group: 'operaciones' },
  { id: 'ventas', label: 'Ventas', icon: 'ShoppingCart', defaultVisible: true, group: 'ventas' },
  { id: 'clientes', label: 'Clientes', icon: 'Users', defaultVisible: true, group: 'ventas' },
  { id: 'mensajes', label: 'Mensajes', icon: 'MessageCircle', defaultVisible: true, group: 'ventas' },
  /** Reportes PDF: visible bajo la sección BI en el sidebar (no en Módulos). */
  { id: 'reportes', label: 'Reportes PDF', icon: 'FileText', defaultVisible: true, group: 'analytics' },
  { id: 'business_intelligence', label: 'Business Intelligence', icon: 'BarChart2', defaultVisible: true, group: 'analytics' },
  { id: 'configuracion', label: 'Configuración', icon: 'Settings', defaultVisible: true, group: 'config' },
  { id: 'usuarios', label: 'Usuarios', icon: 'UserCog', defaultVisible: true, group: 'config' },
];

/** Módulos que el usuario puede mostrar/ocultar en el menú (excluye retirados). */
export function modulesForVisibilityUi(): ModuleDef[] {
  return ALL_MODULES.filter((m) => !RETIRED_MODULE_IDS.includes(m.id));
}

type VisibilityMap = Record<string, boolean>;

export function useModuleVisibility() {
  const authUser = useAuthStore((s) => s.authUser);
  const userId = authUser?.id ?? null;

  const buildDefault = useCallback((): VisibilityMap => {
    const map: VisibilityMap = {};
    ALL_MODULES.forEach((m) => {
      map[m.id] = m.defaultVisible;
    });
    RETIRED_MODULE_IDS.forEach((id) => {
      map[id] = false;
    });
    return map;
  }, []);

  const [visibilityMap, setVisibilityMap] = useState<VisibilityMap>(buildDefault);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setVisibilityMap(buildDefault());
      setIsLoading(false);
      return;
    }
    const load = async () => {
      const { data, error } = await supabase
        .from('user_module_visibility')
        .select('module_id, visible')
        .eq('user_id', userId);
      if (!error && data) {
        const map = buildDefault();
        data.forEach((row) => {
          if (row.module_id in map) {
            map[row.module_id] = row.visible;
          }
        });
        RETIRED_MODULE_IDS.forEach((id) => {
          map[id] = false;
        });
        setVisibilityMap(map);
      }
      setIsLoading(false);
    };
    load();
  }, [userId, buildDefault]);

  const toggleModule = useCallback(
    async (moduleId: string) => {
      if (!userId) {
        logger.warn('Module visibility: no userId (authUser) — ensure session sets authUser in checkSession');
        return;
      }
      const newValue = !visibilityMap[moduleId];
      setVisibilityMap((prev) => ({ ...prev, [moduleId]: newValue }));
      const { error } = await supabase.from('user_module_visibility').upsert(
        {
          user_id: userId,
          module_id: moduleId,
          visible: newValue,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,module_id' }
      );
      if (error) {
        logger.error('Module visibility: failed to save', { moduleId, newValue, error: error.message });
        setVisibilityMap((prev) => ({ ...prev, [moduleId]: !newValue }));
      }
    },
    [userId, visibilityMap]
  );

  return {
    visibilityMap,
    visibleModules: modulesForVisibilityUi().filter((m) => visibilityMap[m.id] !== false),
    hiddenModules: modulesForVisibilityUi().filter((m) => visibilityMap[m.id] === false),
    isLoading,
    toggleModule,
    isVisible: (id: string) => {
      if (RETIRED_MODULE_IDS.includes(id)) return false;
      return visibilityMap[id] !== false;
    },
  };
}
